import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';
import { useUnreadConnections, useContactExchangeLive } from '@/hooks/useConnections';
import {
    Home, User, Shield, Lock, Eye, BarChart3, LogOut,
    Menu, X, ChevronDown, Bell, HelpCircle, Mail,
    ExternalLink, Contact, ShoppingBag
} from 'lucide-react';
import { MetalUpgradeCard, MetalUpgradeBanner, shopUrl } from '@/components/MetalUpgradePromo';

// Same hex mark as nordic3d.us
const HexMark = ({ className = '' }) => (
    <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
        <path d="M16 2.5l11.7 6.75v13.5L16 29.5 4.3 22.75V9.25L16 2.5z" stroke="#35d6ff" strokeWidth="2" />
        <path d="M16 9l6 3.5v7L16 23l-6-3.5v-7L16 9z" fill="#35d6ff" />
    </svg>
);

const Badge = ({ count, className = '' }) =>
    count > 0 ? (
        <span
            className={`inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white ${className}`}
            aria-label={`${count} unread`}
        >
            {count > 99 ? '99+' : count}
        </span>
    ) : null;

const UserLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuthStore();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const { data: unreadConnections = 0 } = useUnreadConnections();

    // Dark Nordic3D look for the whole user area (incl. toasts and pop-ups rendered outside the layout)
    useEffect(() => {
        document.documentElement.classList.add('n3d-dark');
        return () => document.documentElement.classList.remove('n3d-dark');
    }, []);

    // Close menus when the page changes
    useEffect(() => {
        setMobileMenuOpen(false);
        setProfileDropdownOpen(false);
    }, [location.pathname]);

    // Real-time: someone shared their contact from this user's card
    useContactExchangeLive(user?._id, (data) => {
        if (location.pathname === '/user/connections') return;
        toast.success(`${data?.name || 'Someone'} shared their contact with you`, { duration: 5000 });
    });

    const navigation = [
        { name: 'Dashboard', href: '/user/dashboard', icon: Home },
        { name: 'Profile', href: '/user/profile', icon: User },
        { name: 'Card Preview', href: '/user/card-preview', icon: Eye },
        { name: 'Connections', href: '/user/connections', icon: Contact, badge: unreadConnections },
        { name: 'Analytics', href: '/user/analytics', icon: BarChart3 },
        { name: 'Security', href: '/user/security', icon: Shield },
    ];

    const profileMenuItems = [
        { name: 'View Public Card', href: `/card/${user?.cardUuid}`, icon: ExternalLink, external: true }, // Fixed: cardUuid instead of uuid
        { name: 'Email Signature', href: '/user/email-signature', icon: Mail },
        { name: 'Change Password', href: '/user/change-password', icon: Lock },
        { name: 'Shop Nordic3D', href: shopUrl('/shop.html', 'profile_menu'), icon: ShoppingBag, external: true },
        { name: 'Help & Support', href: 'mailto:info@nordic3d.us?subject=HexLink%20help', icon: HelpCircle, external: true },
    ];

    const isActive = (href) => {
        return location.pathname === href;
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="n3d-shell flex min-h-screen flex-col">
            {/* Navigation: full width, like nordic3d.us */}
            <nav className="sticky top-0 z-40 w-full border-b border-gray-200 bg-[#07090d]/85 backdrop-blur-xl">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        {/* Logo and Desktop Navigation */}
                        <div className="flex min-w-0">
                            <div className="flex-shrink-0 flex items-center">
                                <Link to="/user/dashboard" className="flex items-center gap-2.5" aria-label="HexLink dashboard">
                                    <HexMark className="h-8 w-8" />
                                    <span className="leading-none">
                                        <span className="block text-lg font-bold tracking-tight text-gray-900">
                                            Hex<span className="text-primary-600">Link</span>
                                        </span>
                                        <span className="block font-techmono text-[10px] uppercase tracking-[0.18em] text-gray-500">by Nordic3D</span>
                                    </span>
                                </Link>
                            </div>

                            {/* Desktop Navigation */}
                            <div className="hidden md:ml-6 md:flex md:items-center md:space-x-1 lg:ml-10">
                                {navigation.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`inline-flex items-center whitespace-nowrap px-2.5 xl:px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                isActive(item.href)
                                                    ? 'text-primary-600 bg-primary-50'
                                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                                            }`}
                                        >
                                            <Icon className="hidden xl:block h-4 w-4 mr-2" />
                                            {item.name}
                                            <Badge count={item.badge} className="ml-1.5" />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right side items */}
                        <div className="hidden md:ml-4 md:flex md:items-center space-x-2 xl:space-x-3">
                            <a
                                href={shopUrl('/shop.html', 'header')}
                                target="_blank"
                                rel="noopener"
                                className="hidden lg:inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-semibold text-gray-900 transition hover:border-primary-500 hover:text-primary-600"
                            >
                                <ShoppingBag className="h-4 w-4" />
                                Shop
                            </a>
                            {/* Notifications */}
                            <Link
                                to="/user/connections"
                                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                title={unreadConnections ? `${unreadConnections} new connection${unreadConnections === 1 ? '' : 's'}` : 'Connections'}
                            >
                                <Bell className="h-5 w-5" />
                                {unreadConnections > 0 && (
                                    <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full"></span>
                                )}
                            </Link>

                            {/* Profile Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                                    className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    <div className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 transition-colors">
                                        {user?.profile?.profilePicture ? (
                                            <img
                                                className="h-8 w-8 rounded-full object-cover"
                                                src={user.profile.profilePicture}
                                                alt={user?.profile?.name || 'Profile'}
                                            />
                                        ) : (
                                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                <span className="text-sm font-medium text-primary-600">
                                                    {user?.profile?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                                </span>
                                            </div>
                                        )}
                                        <div className="hidden 2xl:block text-left">
                                            <p className="text-sm font-medium text-gray-900">
                                                {user?.profile?.name || 'User'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {user?.email}
                                            </p>
                                        </div>
                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                    </div>
                                </button>

                                {/* Dropdown Menu */}
                                {profileDropdownOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setProfileDropdownOpen(false)}
                                        />
                                        <div className="origin-top-right absolute right-0 mt-2 w-60 rounded-xl shadow-lg bg-white z-20 overflow-hidden">
                                            <div className="py-1">
                                                <div className="px-4 py-2 border-b border-gray-100">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {user?.profile?.name || 'User'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {user?.email}
                                                    </p>
                                                </div>

                                                {profileMenuItems.map((item) => {
                                                    const Icon = item.icon;
                                                    if (item.external) {
                                                        return (
                                                            <a
                                                                key={item.name}
                                                                href={item.href}
                                                                target={item.href.startsWith('mailto:') ? undefined : '_blank'}
                                                                rel="noopener"
                                                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                onClick={() => setProfileDropdownOpen(false)}
                                                            >
                                                                <Icon className="h-4 w-4 mr-3 text-gray-400" />
                                                                {item.name}
                                                            </a>
                                                        );
                                                    }
                                                    return (
                                                        <Link
                                                            key={item.name}
                                                            to={item.href}
                                                            onClick={() => setProfileDropdownOpen(false)}
                                                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                        >
                                                            <Icon className="h-4 w-4 mr-3 text-gray-400" />
                                                            {item.name}
                                                        </Link>
                                                    );
                                                })}

                                                <div className="border-t border-gray-100">
                                                    <button
                                                        onClick={handleLogout}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        <LogOut className="h-4 w-4 mr-3 text-gray-400" />
                                                        Sign out
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Mobile menu button */}
                        <div className="flex items-center md:hidden">
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                            >
                                {mobileMenuOpen ? (
                                    <X className="h-6 w-6" />
                                ) : (
                                    <span className="relative">
                                        <Menu className="h-6 w-6" />
                                        {unreadConnections > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-red-500 rounded-full ring-2 ring-[#07090d]"></span>
                                        )}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-gray-200 bg-[#07090d]">
                        <div className="pt-2 pb-3 space-y-1">
                            {navigation.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center px-4 py-2 text-base font-medium transition-colors ${
                                            isActive(item.href)
                                                ? 'text-primary-600 bg-primary-50 border-l-4 border-primary-600'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon className="h-5 w-5 mr-3" />
                                        {item.name}
                                        <Badge count={item.badge} className="ml-2" />
                                    </Link>
                                );
                            })}
                        </div>

                        <div className="pt-4 pb-3 border-t border-gray-200">
                            <div className="flex items-center px-4 mb-3">
                                {user?.profile?.profilePicture ? (
                                    <img
                                        className="h-10 w-10 rounded-full object-cover"
                                        src={user.profile.profilePicture}
                                        alt={user?.profile?.name || 'Profile'}
                                    />
                                ) : (
                                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                        <span className="text-lg font-medium text-primary-600">
                                            {user?.profile?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                        </span>
                                    </div>
                                )}
                                <div className="ml-3">
                                    <div className="text-base font-medium text-gray-800">
                                        {user?.profile?.name || 'User'}
                                    </div>
                                    <div className="text-sm font-medium text-gray-500">
                                        {user?.email}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                {profileMenuItems.map((item) => {
                                    const Icon = item.icon;
                                    if (item.external) {
                                        return (
                                            <a
                                                key={item.name}
                                                href={item.href}
                                                target={item.href.startsWith('mailto:') ? undefined : '_blank'}
                                                rel="noopener"
                                                className="flex items-center px-4 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <Icon className="h-5 w-5 mr-3 text-gray-400" />
                                                {item.name}
                                            </a>
                                        );
                                    }
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="flex items-center px-4 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                        >
                                            <Icon className="h-5 w-5 mr-3 text-gray-400" />
                                            {item.name}
                                        </Link>
                                    );
                                })}

                                <button
                                    onClick={handleLogout}
                                    className="flex items-center w-full px-4 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                >
                                    <LogOut className="h-5 w-5 mr-3 text-gray-400" />
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Page content, with the HexLink Metal upgrade on the left (desktop) */}
            <div className="flex w-full flex-1 gap-6 px-4 py-6 sm:px-6 lg:px-8 xl:gap-8">
                <aside className="hidden w-64 flex-none lg:block xl:w-72" aria-label="HexLink Metal">
                    <div className="sticky top-24 space-y-4">
                        <MetalUpgradeCard user={user} />
                        <nav className="rounded-2xl border border-gray-200 bg-white/60 p-4 text-sm" aria-label="Nordic3D shop">
                            <p className="mb-2 font-techmono text-[11px] uppercase tracking-[0.16em] text-gray-500">More from Nordic3D</p>
                            <ul className="space-y-1">
                                {[
                                    ['HexLink Keychain', '/product.html?handle=hexlink-dbc-keychain'],
                                    ['Order more cards', '/hexlink.html#lineup'],
                                    ['Team & bulk orders', '/business.html'],
                                    ['Laser engraving', '/engraving.html'],
                                ].map(([label, path]) => (
                                    <li key={path}>
                                        <a
                                            href={shopUrl(path, 'sidebar_links')}
                                            target="_blank"
                                            rel="noopener"
                                            className="flex items-center justify-between rounded-lg px-2 py-1.5 text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            {label}
                                            <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </aside>

                <main className="n3d-main min-w-0 flex-1">
                    <MetalUpgradeBanner user={user} className="mb-5 lg:hidden" />
                    <Outlet />
                </main>
            </div>

            {/* Footer */}
            <footer className="border-t border-gray-200 bg-[#07090d]">
                <div className="flex w-full flex-col gap-4 px-4 py-6 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2.5 text-gray-500">
                        <HexMark className="h-5 w-5" />
                        <span>&copy; {new Date().getFullYear()} Nordic3D. Handcrafted in Wisconsin.</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <a href={shopUrl('/', 'footer')} className="text-gray-500 hover:text-gray-900">nordic3d.us</a>
                        <a href={shopUrl('/shop.html', 'footer')} className="text-gray-500 hover:text-gray-900">Shop</a>
                        <a href="https://nordic3d.us/privacy.html" className="text-gray-500 hover:text-gray-900">Privacy</a>
                        <a href="https://nordic3d.us/terms.html" className="text-gray-500 hover:text-gray-900">Terms</a>
                        <a href="mailto:info@nordic3d.us?subject=HexLink%20help" className="text-gray-500 hover:text-gray-900">Help</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default UserLayout;